import React, { useState, useMemo } from 'react';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useApiClient, ComboBoxOption, useFetchImmutable } from '@marageti/z4-lib';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { CountrySetting } from '@marageti/z4-sdk/lib/travel';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useSnackbar } from 'notistack';
import CountryDefaultsView from './country-defaults-view';

const RISK_MAP = {
  A: 'No Risk',
  B: 'Low Risk: Use Discretion',
  C: 'Medium Risk: Need More Information',
  D: 'High Risk',
  E: 'Auto-Junk',
};

const addOrUpdateCountrySettings = (cs: CountrySetting[], newSetting: CountrySetting): CountrySetting[] => {
  const updated = [...cs];
  const existingIndex = updated.findIndex((e) => e.isoCode2 === newSetting.isoCode2);
  if (existingIndex >= 0) {
    updated[existingIndex] = newSetting;
    return updated;
  }
  updated.push(newSetting);
  return updated;
};

const formatCountryOptions = (obj: any): ComboBoxOption[] => {
  if (!obj) {
    return [{ label: 'loading', value: '' }];
  }
  const ret: ComboBoxOption[] = [];
  Object.keys(obj).forEach((key) => {
    const opt = {
      label: obj[key],
      value: key,
    };
    ret.push(opt);
  });
  return ret.sort((a, b) => a.label.localeCompare(b.label));
};

const getDefault = (array: any[] = []) => array.find((cs: CountrySetting) => cs.country === 'Default');
const getRest = (array: any[] = []) => array.filter((cs: CountrySetting) => cs.country !== 'Default');

const formatFormData = (data: CountrySetting) => ({
  ...data,
  country: data.country,
  minBudgetPPPD: Number(data.minBudgetPPPD),
  minNights: Number(data.minNights),
  minCharCount: Number(data.minCharCount),
  maxMonths: Number(data.maxMonths),
});

const sortCountrySettings = (settings: CountrySetting[]): CountrySetting[] => settings.sort((a, b) => a.country.localeCompare(b.country));

const filterOptions = (settings: CountrySetting[], options: ComboBoxOption[]) => {
  const ret = options.filter((ele) => {
    const test = ele.value;
    return !settings.find((e) => e.isoCode2 === test);
  });
  return ret;
};

const CountryDefaults = () => {
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: countrySettings, error, mutate } = useFetchImmutable<CountrySetting[]>(apiClient.countrySettingsClient.getCountrySettings, [], true);
  if (error?.code === 401) navigate('/logout');

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm();

  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [editorState, setEditorState] = useState<any>({});
  const [settingToDelete, setSettingToDelete] = useState<CountrySetting>();
  const [submissionError, setSubmissionError] = useState<ApiError>();

  const countryDefault = useMemo(() => getDefault(countrySettings), [countrySettings]);

  const countryOptions = useMemo(() => {
    const options = formatCountryOptions(countryCodes);
    return filterOptions(countrySettings || [], options);
  }, [countryCodes, countrySettings]);

  const countryList = useMemo(
    () => sortCountrySettings(getRest(countrySettings)),
    [countrySettings, countryCodes],
  );

  const riskLevelOptions:ComboBoxOption[] = [
    {
      label: 'No Risk',
      value: 'A',
    },
    {
      label: 'Low Risk: Use Discretion',
      value: 'B',
    },
    {
      label: 'Medium Risk: Need More Information',
      value: 'C',
    },
    {
      label: 'High Risk',
      value: 'D',
    },
    {
      label: 'Auto-Junk',
      value: 'E',
    },
  ];

  const setFormValues = (countrySetting: CountrySetting) => {
    setValue('country', countrySetting.country || '');
    setValue('minBudgetPPPD', countrySetting.minBudgetPPPD);
    setValue('minNights', countrySetting.minNights);
    setValue('minCharCount', countrySetting.minCharCount);
    setValue('maxMonths', countrySetting.maxMonths);
    const riskOption = countrySetting.originRiskRating
      ? { value: countrySetting.originRiskRating, label: RISK_MAP[countrySetting.originRiskRating as keyof typeof RISK_MAP] }
      : { value: countryDefault?.originRiskRating || 'C', label: RISK_MAP[countryDefault?.originRiskRating as keyof typeof RISK_MAP] || RISK_MAP.C };
    setValue('originRiskRating', riskOption);
  };

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const update = !!editorState.country;
    const merged = {
      ...editorState,
      ...data,
      originRiskRating: data.originRiskRating.value,
      country: data.country.value ? data.country.label : editorState?.country,
      isoCode2: data.country.value ? data.country.value : editorState?.isoCode2,
    };
    const req: CountrySetting = formatFormData(merged);
    let prom;
    if (update) {
      prom = apiClient.countrySettingsClient.putCountrySetting(req.isoCode2, req);
    } else {
      prom = apiClient.countrySettingsClient.postCountrySetting(req);
    }
    prom
      .then((res) => {
        setShowModal(false);
        setEditorState({});
        const defaultSetting = countryDefault;
        reset({
          country: '',
          minBudgetPPPD: defaultSetting.minBudgetPPPD,
          minNights: defaultSetting.minNights,
          maxMonths: countryDefault?.maxMonths || 0,
          minCharCount: countryDefault?.minCharCount || 0,
          originRiskRating: { value: countryDefault?.originRiskRating || 'C', label: RISK_MAP[countryDefault?.originRiskRating as keyof typeof RISK_MAP] || RISK_MAP.C },
        });
        mutate(addOrUpdateCountrySettings(countrySettings || [], res));
        enqueueSnackbar('Country setting saved.', { variant: 'success' });
      })
      .catch((e: ApiError) => {
        if (e.code === 401) navigate('/logout');
        setSubmissionError(e);
      });
  };

  const onClickEdit = (countrySetting: CountrySetting) => {
    setEditorState(countrySetting);
    setFormValues(countrySetting);
    setShowModal(true);
  };

  const onClickDelete = (countrySetting: CountrySetting) => {
    setSettingToDelete(countrySetting);
  };

  const onConfirmDelete = () => {
    apiClient.countrySettingsClient
      .deleteCountrySetting(settingToDelete?.isoCode2 as string)
      .then(() => {
        setSettingToDelete(undefined);
        mutate(countrySettings?.filter((cs: CountrySetting) => cs.country !== settingToDelete?.country));
        enqueueSnackbar('Country setting deleted.', { variant: 'success' });
      })
      .catch((e: ApiError) => {
        if (e.code === 401) navigate('/logout');
        setSubmissionError(e);
      });
  };

  const onCancelDelete = () => setSettingToDelete(undefined);

  const closeModal = () => {
    setShowModal(false);
    setEditorState({});
    setSubmissionError(undefined);
    reset({
      country: '',
      minBudgetPPPD: countryDefault?.minBudgetPPPD || 0,
      minNights: countryDefault?.minNights || 0,
      maxMonths: countryDefault?.maxMonths || 0,
      minCharCount: countryDefault?.minCharCount || 0,
      originRiskRating: { value: countryDefault?.originRiskRating || 'C', label: RISK_MAP[countryDefault?.originRiskRating as keyof typeof RISK_MAP] || RISK_MAP.C },
    });
  };

  const onClickCreate = () => {
    setShowModal(true);
    setEditorState({});
    reset({
      country: '',
      isoCode2: '',
      maxMonths: countryDefault?.maxMonths || 0,
      minBudgetPPPD: countryDefault?.minBudgetPPPD || 0,
      minCharCount: countryDefault?.minCharCount || 0,
      minNights: countryDefault?.minNights || 0,
      originRiskRating: { value: countryDefault?.originRiskRating || 'C', label: RISK_MAP[countryDefault?.originRiskRating as keyof typeof RISK_MAP] || RISK_MAP.C },
    });
  };

  return countrySettings ? (
    <CountryDefaultsView
      riskLevelOptions={riskLevelOptions}
      control={control}
      countryDefault={countryDefault}
      countryList={countryList}
      countryOptions={countryOptions}
      editorState={editorState}
      error={error}
      errors={errors}
      handleSubmit={handleSubmit}
      onCancelDelete={onCancelDelete}
      onClickCreate={onClickCreate}
      onClickDelete={onClickDelete}
      onClickEdit={onClickEdit}
      onClose={closeModal}
      onConfirmDelete={onConfirmDelete}
      onSubmit={onSubmit}
      register={register}
      settingToDelete={settingToDelete}
      showModal={showModal}
      submissionError={submissionError}
    />
  ) : null;
};

export default CountryDefaults;
